/**
 * <video-band> — the homepage motion band. docs/54 §1.-32.
 *
 * Two jobs, and both are about NOT playing rather than about playing.
 *
 * 1. REDUCED MOTION IS ENFORCED BEFORE THE FIRST FRAME.
 *
 *    `autoplay` is an HTML attribute. A media query cannot un-fire it, and
 *    hiding the element in CSS still downloads and still decodes the file —
 *    the whole cost with none of the effect, which is the worst of both. The
 *    attribute is removed and the element paused here instead, so the poster
 *    is what a reduced-motion visitor gets and the video never starts.
 *
 *    Checked live rather than once: someone who turns the preference ON while
 *    the page is open should have the motion stop, which is the behaviour the
 *    preference promises.
 *
 * 2. IT DOES NOT PLAY OFF-SCREEN.
 *
 *    This band sits at the foot of a long homepage. A looping video decoding
 *    continuously while it is nowhere near the viewport is pure battery cost
 *    on the device least able to afford it. An IntersectionObserver pauses it
 *    when it leaves and resumes it when it returns — unless the visitor has
 *    pressed pause, whose intent outranks the observer's and is what
 *    `userPaused` exists to remember.
 *
 * NO AUTOPLAY PROMISE IS ASSUMED TO RESOLVE. `play()` returns a promise that
 * REJECTS on a browser that declines it, and an unhandled rejection in a
 * theme is a console error on a merchant's storefront. Every call is caught.
 */

class VideoBand extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('video');
    this.toggle = this.querySelector('[data-video-toggle]');
    this.label = this.querySelector('[data-video-toggle-label]');
    if (!this.video) return;

    /* The visitor's own pause. Distinct from every automatic pause below, so
       scrolling away and back cannot silently undo a deliberate choice. */
    this.userPaused = false;

    /* IN VIEW GATES PLAYBACK, AND THEREFORE GATES THE DOWNLOAD — 2026-08-22.
       The <video> lost its `autoplay` and took `preload="none"` because the
       footer band was pulling 3,102 KB before first paint (54% of the page) for
       a band nobody had scrolled to. But the markup alone does not fix it:
       `connectedCallback` calls `onMotionChange()` a few lines below, which
       calls `resume()` -> `play()` — and `play()` starts the download no matter
       what the preload hint says. That ran BEFORE the observer was attached, so
       the video downloaded on load exactly as before.

       So playback now has to be asked for twice: motion allowed AND the band
       actually near the viewport. The observer below owns the second half.

       Defaults TRUE when IntersectionObserver is missing — without it there is
       nothing to ever flip the flag, and a permanently black band would be a
       worse failure than an eager download. */
    this.inView = !('IntersectionObserver' in window);

    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.onMotionChange = this.onMotionChange.bind(this);
    this.onToggle = this.onToggle.bind(this);

    this.onMotionChange();
    /* Safari < 14 has addListener only. */
    if (this.motionQuery.addEventListener) {
      this.motionQuery.addEventListener('change', this.onMotionChange);
    } else if (this.motionQuery.addListener) {
      this.motionQuery.addListener(this.onMotionChange);
    }

    if (this.toggle) this.toggle.addEventListener('click', this.onToggle);

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.hydratePoster();
              this.resume();
            } else {
              this.video.pause();
            }
          });
        },
        { rootMargin: '200px 0px' },
      );
      this.observer.observe(this);
    } else {
      /* No observer means `inView` is already true and playback is not gated,
         so the poster must not be gated either -- otherwise the band renders
         empty forever on the one browser that cannot defer anything. */
      this.hydratePoster();
    }
  }

  /**
   * THE POSTER IS THE HALF `preload="none"` DOES NOT COVER.
   *
   * That attribute governs the video file. The poster is an ordinary image
   * fetch and happens on load regardless — measured on live at 86KB
   * (`width=1600`) at +213ms on a 390px viewport, for a decorative band far
   * below the fold. The video was already gated behind the observer by the
   * 3,102KB fix recorded at the top of this file; this closes the other half.
   *
   * Runs on the first intersection, from `data-poster` / `data-poster-mobile`.
   * Two widths because `poster` accepts no srcset and a phone has no use for
   * 1600px. Idempotent: the attributes are removed once promoted, so the
   * repeated intersections a visitor generates by scrolling cost nothing.
   */
  hydratePoster() {
    if (!this.video || this.posterDone) return;
    this.posterDone = true;
    const wide = window.matchMedia('(min-width: 768px)').matches;
    const url = (wide ? null : this.video.getAttribute('data-poster-mobile')) || this.video.getAttribute('data-poster');
    if (url) this.video.setAttribute('poster', url);
    this.video.removeAttribute('data-poster');
    this.video.removeAttribute('data-poster-mobile');
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this.motionQuery?.removeEventListener) {
      this.motionQuery.removeEventListener('change', this.onMotionChange);
    } else if (this.motionQuery?.removeListener) {
      this.motionQuery.removeListener(this.onMotionChange);
    }
  }

  /**
   * True when playback must NOT start: motion is reduced, the visitor pressed
   * pause, or the band is not near the viewport yet.
   *
   * The third clause is the one that keeps 3 MB off the initial load — with
   * `preload="none"` the file is not fetched until `play()` is called, so
   * refusing to call it is the whole mechanism.
   */
  get shouldHold() {
    return this.motionQuery.matches || this.userPaused || !this.inView;
  }

  onMotionChange() {
    if (this.motionQuery.matches) {
      this.video.removeAttribute('autoplay');
      this.video.pause();
      this.classList.add('video-band--still');
    } else {
      this.classList.remove('video-band--still');
      this.resume();
    }
    this.syncToggle();
  }

  resume() {
    if (this.shouldHold) return;
    const p = this.video.play();
    /* Rejects when the browser declines autoplay. Nothing to recover — the
       poster is already the frame underneath — but it must not surface as an
       unhandled rejection on a merchant's storefront. */
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }

  onToggle() {
    this.userPaused = !this.userPaused;
    if (this.userPaused) {
      this.video.pause();
    } else {
      this.resume();
    }
    this.syncToggle();
  }

  /**
   * The accessible name states what the control WILL DO, not what the video
   * is doing — "Play video" while paused. `aria-pressed` carries the state so
   * the two are never saying the same thing twice.
   */
  syncToggle() {
    if (!this.toggle) return;
    const held = this.shouldHold;
    this.toggle.setAttribute('aria-pressed', held ? 'true' : 'false');
    this.classList.toggle('video-band--paused', held);
    if (this.label) {
      this.label.textContent = this.toggle.dataset[held ? 'labelPlay' : 'labelPause'] || this.label.textContent;
    }
  }
}

if (!customElements.get('video-band')) customElements.define('video-band', VideoBand);
